import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminAutomationTemplateListQuery,
  Automation,
  AutomationTemplate,
  AutomationTemplateListItem,
  AutomationTemplateListQuery,
  CollectionMeta,
  CreateAutomationTemplateInput,
  UpdateAutomationTemplateInput,
  UseAutomationTemplateInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['automation-templates'] as const;
const ADMIN_KEY = ['admin', 'automation-templates'] as const;

export interface AutomationTemplateListPage {
  items: AutomationTemplateListItem[];
  meta: CollectionMeta;
}

// --- Tenant-facing ---------------------------------------------------

function toQuery(q: AutomationTemplateListQuery): Record<string, string | number | undefined> {
  return { page: q.page, pageSize: q.pageSize, category: q.category };
}

export async function listAutomationTemplates(
  q: AutomationTemplateListQuery = {},
): Promise<AutomationTemplateListPage> {
  const res = await apiClient.getCollection<AutomationTemplateListItem>('/automation-templates', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}
export const getAutomationTemplate = (id: string) =>
  apiClient.get<AutomationTemplate>(`/automation-templates/${id}`);
export const useAutomationTemplateAction = (id: string, input: UseAutomationTemplateInput) =>
  apiClient.post<Automation>(`/automation-templates/${id}/use`, input);

export function useAutomationTemplates(query: AutomationTemplateListQuery = {}) {
  return useQuery<AutomationTemplateListPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listAutomationTemplates(query),
  });
}

export function useAutomationTemplate(id: string | undefined) {
  return useQuery<AutomationTemplate, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getAutomationTemplate(id!),
    enabled: Boolean(id),
  });
}

/** "Using" a template creates a normal automation - invalidate the automations list too. */
export function useUseAutomationTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation<Automation, ApiClientError, UseAutomationTemplateInput>({
    mutationFn: (input) => useAutomationTemplateAction(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
  });
}

// --- Platform admin ----------------------------------------------------

function toAdminQuery(
  q: AdminAutomationTemplateListQuery,
): Record<string, string | number | undefined> {
  return { page: q.page, pageSize: q.pageSize, category: q.category, status: q.status };
}

export async function listAdminAutomationTemplates(
  q: AdminAutomationTemplateListQuery = {},
): Promise<AutomationTemplateListPage> {
  const res = await apiClient.getCollection<AutomationTemplateListItem>(
    '/admin/automation-templates',
    { query: toAdminQuery(q) },
  );
  return { items: res.data, meta: res.meta };
}
export const getAdminAutomationTemplate = (id: string) =>
  apiClient.get<AutomationTemplate>(`/admin/automation-templates/${id}`);
export const createAutomationTemplate = (input: CreateAutomationTemplateInput) =>
  apiClient.post<AutomationTemplate>('/admin/automation-templates', input);
export const updateAutomationTemplate = (id: string, input: UpdateAutomationTemplateInput) =>
  apiClient.patch<AutomationTemplate>(`/admin/automation-templates/${id}`, input);
export const deleteAutomationTemplate = (id: string) =>
  apiClient.delete<void>(`/admin/automation-templates/${id}`);

export function useAdminAutomationTemplates(query: AdminAutomationTemplateListQuery = {}) {
  return useQuery<AutomationTemplateListPage, ApiClientError>({
    queryKey: [...ADMIN_KEY, 'list', query],
    queryFn: () => listAdminAutomationTemplates(query),
  });
}

export function useAdminAutomationTemplate(id: string | undefined) {
  return useQuery<AutomationTemplate, ApiClientError>({
    queryKey: [...ADMIN_KEY, 'detail', id],
    queryFn: () => getAdminAutomationTemplate(id!),
    enabled: Boolean(id),
  });
}

export function useCreateAutomationTemplate() {
  const qc = useQueryClient();
  return useMutation<AutomationTemplate, ApiClientError, CreateAutomationTemplateInput>({
    mutationFn: createAutomationTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_KEY });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateAutomationTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation<AutomationTemplate, ApiClientError, UpdateAutomationTemplateInput>({
    mutationFn: (input) => updateAutomationTemplate(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_KEY });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteAutomationTemplate() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteAutomationTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_KEY });
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
